import { ArrowRight, CheckCircle2 } from "lucide-react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { IMAGES } from "../lib/content";
import {
  SectionHeader,
  PrimaryLink,
  Disclaimer,
} from "../components/ui-blocks";
import HeroSlideshow from "../components/HeroSlideshow";

export default function Home() {
  const { t } = useTranslation();
  const services = t("data.services", { returnObjects: true });
  const opportunities = t("data.opportunities", { returnObjects: true });
  const why = t("data.whyMtbl", { returnObjects: true });
  const highlights = t("data.highlights", { returnObjects: true });

  return (
    <>
      {/* LANDSCAPE SLIDESHOW */}
      <HeroSlideshow />

      {/* POSITIONING LINE */}
      <section
        data-testid="home-positioning"
        className="bg-paper border-y border-slate-200"
      >
        <div className="container-mtbl py-14 md:py-20">
          <p className="text-[#0B1A3D] text-xl md:text-3xl lg:text-4xl tracking-tight font-semibold leading-snug max-w-5xl">
            {t("home.positioning1")}{" "}
            <span className="text-slate-500">{t("home.positioning2")}</span>
          </p>
        </div>
      </section>

      {/* WHAT WE DO */}
      <section data-testid="home-whatwedo" className="section-pad">
        <div className="container-mtbl grid lg:grid-cols-12 gap-14 items-start">
          <div className="lg:col-span-5">
            <SectionHeader
              overline={t("home.whatWeDoOverline")}
              title={t("home.whatWeDoTitle")}
            />
          </div>
          <div className="lg:col-span-7 space-y-6 text-slate-600 text-base md:text-lg leading-relaxed">
            <p>{t("home.whatWeDoP1")}</p>
            <p>{t("home.whatWeDoP2")}</p>
            <div className="pt-4">
              <Link
                to="/about"
                data-testid="home-whatwedo-learn-more"
                className="btn-ghost"
              >
                {t("home.whatWeDoLearnMore")} <ArrowRight size={16} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section
        data-testid="home-services"
        className="section-pad bg-paper border-t border-slate-200"
      >
        <div className="container-mtbl">
          <SectionHeader
            overline={t("home.servicesOverline")}
            title={t("home.servicesTitle")}
          />
          <div className="mt-14 grid md:grid-cols-2 gap-6">
            {services.map((s, i) => (
              <div
                key={s.title}
                data-testid={`home-service-${i}`}
                className="card-soft p-8 md:p-10"
              >
                <p className="overline">0{i + 1}</p>
                <h3 className="mt-3 text-[#0B1A3D] text-xl md:text-2xl font-semibold tracking-tight">
                  {s.title}
                </h3>
                <p className="mt-4 text-slate-600 leading-relaxed">{s.body}</p>
              </div>
            ))}
          </div>
          <Disclaimer testId="home-services-disclaimer">
            {t("home.servicesDisclaimer")}
          </Disclaimer>
        </div>
      </section>

      {/* SELECTED OPPORTUNITIES */}
      <section
        data-testid="home-opportunities"
        className="section-pad bg-paper border-t border-slate-200"
      >
        <div className="container-mtbl">
          <SectionHeader
            overline={t("home.opportunitiesOverline")}
            title={t("home.opportunitiesTitle")}
            lead={t("home.opportunitiesLead")}
          />
          <div className="mt-14 grid md:grid-cols-2 gap-6">
            {opportunities.map((opp, i) => (
              <div
                key={opp.title}
                data-testid={`home-opportunity-${i}`}
                className="group card-soft overflow-hidden"
              >
                <div className="relative aspect-[16/9] overflow-hidden">
                  <img
                    src={IMAGES[opp.img]}
                    alt={opp.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  />
                </div>
                <div className="p-7 md:p-8">
                  <p className="overline">
                    {t("home.opportunityLabel")} · 0{i + 1}
                  </p>
                  <h3 className="mt-3 text-[#0B1A3D] text-xl md:text-2xl font-semibold tracking-tight">
                    {opp.title}
                  </h3>
                  <p className="mt-4 text-slate-600 leading-relaxed text-sm md:text-base">
                    {opp.summary}
                  </p>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-12">
            <PrimaryLink to="/opportunities" testId="home-request-brief-btn">
              {t("common.requestInvestmentBrief")}
            </PrimaryLink>
          </div>
        </div>
      </section>

      {/* STRATEGIC ENGAGEMENT */}
      <section data-testid="home-strategic-engagement" className="section-pad">
        <div className="container-mtbl grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <SectionHeader
              overline={t("home.engagementOverline")}
              title={t("home.engagementTitle")}
            />
          </div>
          <div className="lg:col-span-7 space-y-6 text-slate-600 text-base md:text-lg leading-relaxed">
            <p>{t("home.engagementP1")}</p>
            <p>{t("home.engagementP2")}</p>
          </div>
        </div>
      </section>

      {/* STRATEGIC COLLABORATION */}
      <section
        data-testid="home-strategic-collaboration"
        className="section-pad bg-paper border-t border-slate-200"
      >
        <div className="container-mtbl grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <SectionHeader
              overline={t("home.collaborationOverline")}
              title={t("home.collaborationTitle")}
            />
          </div>
          <div className="lg:col-span-7 space-y-6 text-slate-600 text-base md:text-lg leading-relaxed">
            <p>{t("home.collaborationP1")}</p>
            <p>{t("home.collaborationP2")}</p>
          </div>
        </div>
      </section>

      {/* WHY MTBL */}
      <section data-testid="home-why" className="section-pad">
        <div className="container-mtbl">
          <SectionHeader
            overline={t("home.whyOverline")}
            title={t("home.whyTitle")}
          />
          <ul className="mt-14 grid md:grid-cols-2 gap-x-14 gap-y-4">
            {why.map((item, i) => (
              <li
                key={item}
                data-testid={`home-why-${i}`}
                className="flex items-start gap-4 border-b border-slate-200 py-5"
              >
                <CheckCircle2
                  size={20}
                  className="text-[#0B1A3D] mt-0.5 shrink-0"
                />
                <span className="text-slate-700 text-base md:text-lg">
                  {item}
                </span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* HIGHLIGHTS */}
      <section
        data-testid="home-highlights"
        className="section-pad bg-paper border-t border-slate-200"
      >
        <div className="container-mtbl">
          <SectionHeader
            overline={t("home.highlightsOverline")}
            title={t("home.highlightsTitle")}
          />
          <div className="mt-12 grid md:grid-cols-5 gap-4">
            {highlights.map((h, i) => (
              <div
                key={h.k}
                data-testid={`home-highlight-${i}`}
                className="card-soft p-8"
              >
                <p className="overline">{h.k}</p>
                <p className="mt-4 text-[#0B1A3D] text-xl md:text-2xl font-semibold tracking-tight">
                  {h.v}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* IMPACT */}
      <section data-testid="home-impact" className="section-pad">
        <div className="container-mtbl grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <SectionHeader
              overline={t("home.impactOverline")}
              title={t("home.impactTitle")}
            />
          </div>
          <div className="lg:col-span-7 space-y-6 text-slate-600 text-base md:text-lg leading-relaxed">
            <p>{t("home.impactP1")}</p>
            <p>{t("home.impactP2")}</p>
            <p className="text-slate-500 italic border-l-2 border-slate-300 pl-5">
              {t("home.impactQuote")}
            </p>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section
        data-testid="home-final-cta"
        className="relative overflow-hidden bg-navy"
      >
        <div className="container-mtbl py-24 md:py-32 relative">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-300">
            {t("home.finalCtaOverline")}
          </p>
          <h2 className="mt-6 text-white text-4xl md:text-5xl lg:text-6xl font-bold tracking-tighter leading-[1.05] max-w-4xl">
            {t("home.finalCtaTitle")}
          </h2>
          <p className="mt-8 text-slate-300 text-base md:text-lg max-w-2xl leading-relaxed">
            {t("home.finalCtaLead")}
          </p>
          <div className="mt-10 flex flex-wrap gap-4">
            <Link
              to="/contact?type=investor"
              data-testid="home-cta-investor"
              className="inline-flex items-center justify-center rounded-full bg-white text-[#0B1A3D] px-7 py-3.5 text-sm font-semibold tracking-wide hover:bg-slate-100 transition-colors"
            >
              {t("common.investorInquiries")}
            </Link>
            <Link
              to="/contact?type=strategic"
              data-testid="home-cta-strategic"
              className="inline-flex items-center justify-center rounded-full border border-white/30 text-white px-7 py-3.5 text-sm font-semibold tracking-wide hover:border-white transition-colors"
            >
              {t("common.strategicPartnerships")}
            </Link>
            <Link
              to="/contact?type=institutional"
              data-testid="home-cta-institutional"
              className="inline-flex items-center justify-center rounded-full border border-white/30 text-white px-7 py-3.5 text-sm font-semibold tracking-wide hover:border-white transition-colors"
            >
              {t("common.institutionalEngagement")}
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
